<template>
  <section class="catalog-configs-workspace">
    <article class="catalog-panel">
      <div class="catalog-panel__heading">
        <div>
          <span class="catalog-section-number">01</span>
          <div>
            <h2>Configurações gerais</h2>
            <p>Defina regras de uso e preferências do Catálogo Digital.</p>
          </div>
        </div>
      </div>
      <div class="catalog-checkout-rules">
        <div class="catalog-config-row">
          <div>
            <strong>Permitir pedido sem CPF/CNPJ</strong>
          </div>
          <label class="catalog-toggle">
            <input
              type="checkbox"
              :checked="checkoutSettings.permitir_pedido_sem_documento"
              :disabled="
                loadingCheckoutSettings ||
                !checkoutSettingsLoaded ||
                savingCheckoutRule
              "
              aria-label="Permitir pedido sem CPF/CNPJ"
              @change="
                updateCheckoutRule('permitir_pedido_sem_documento', $event)
              "
            />
            <span class="catalog-toggle__track">
              <span class="catalog-toggle__thumb"></span>
            </span>
          </label>
        </div>

        <div class="catalog-config-row">
          <div>
            <strong>Permitir pedido sem email</strong>
          </div>
          <label class="catalog-toggle">
            <input
              type="checkbox"
              :checked="checkoutSettings.permitir_pedido_sem_email"
              :disabled="
                loadingCheckoutSettings ||
                !checkoutSettingsLoaded ||
                savingCheckoutRule
              "
              aria-label="Permitir pedido sem email"
              @change="updateCheckoutRule('permitir_pedido_sem_email', $event)"
            />
            <span class="catalog-toggle__track">
              <span class="catalog-toggle__thumb"></span>
            </span>
          </label>
        </div>

        <div class="catalog-config-row">
          <div>
            <strong>Aviso sonoro ao receber novo pedido</strong>
          </div>
          <label class="catalog-toggle">
            <input
              type="checkbox"
              :checked="checkoutSettings.som_novos_pedidos"
              :disabled="
                loadingCheckoutSettings ||
                !checkoutSettingsLoaded ||
                savingCheckoutRule
              "
              aria-label="Ativar som ao receber novo pedido"
              @change="updateCheckoutRule('som_novos_pedidos', $event)"
            />
            <span class="catalog-toggle__track">
              <span class="catalog-toggle__thumb"></span>
            </span>
          </label>
        </div>
      </div>
    </article>

    <article class="catalog-panel">
      <div class="catalog-panel__heading">
        <div>
          <span class="catalog-section-number">02</span>
          <div>
            <h2>Formas de pagamento</h2>
            <p>
              Escolha quais formas de pagamento poderão ser selecionadas pelo
              cliente no checkout.
            </p>
          </div>
        </div>
        <button
          type="button"
          class="catalog-button catalog-button--outline"
          :disabled="loadingCheckoutSettings"
          @click="togglePaymentForm"
        >
          {{ creatingPayment ? "Cancelar" : "Nova forma de pagamento" }}
        </button>
      </div>
      <form
        v-if="creatingPayment"
        class="catalog-payment-form"
        @submit.prevent="createPayment"
      >
        <label>
          Descrição
          <input
            v-model.trim="newPayment.descricao"
            type="text"
            maxlength="255"
            required
          />
        </label>
        <label>
          Meio de pagamento
          <select
            v-model.number="newPayment.id_meio_pagamento"
            required
            :disabled="loadingPaymentMeans"
          >
            <option :value="null" disabled>Selecione o meio</option>
            <option
              v-for="meio in paymentMeans"
              :key="meio.id"
              :value="meio.id"
            >
              {{ meio.descricao }}
            </option>
          </select>
        </label>
        <label>
          Parcelas
          <input
            v-model.number="newPayment.vezes"
            type="number"
            min="1"
            max="48"
            required
          />
        </label>
        <label v-if="Number(newPayment.vezes) > 1">
          Intervalo
          <select v-model.number="newPayment.tipo_repeticao" required>
            <option
              v-for="intervalo in paymentIntervals"
              :key="intervalo.id"
              :value="intervalo.id"
            >
              {{ intervalo.descricao }}
            </option>
          </select>
        </label>
        <p
          v-if="paymentFormError"
          class="catalog-payment-form__error"
          role="alert"
        >
          {{ paymentFormError }}
        </p>
        <button
          class="catalog-button"
          type="submit"
          :disabled="savingNewPayment || loadingPaymentMeans"
        >
          {{ savingNewPayment ? "Salvando..." : "Criar forma de pagamento" }}
        </button>
      </form>
      <div v-if="loadingCheckoutSettings" class="catalog-configs-empty">
        Carregando configurações...
      </div>
      <div v-else-if="checkoutSettingsError" class="catalog-configs-empty">
        <p>Não foi possível carregar as configurações.</p>
      </div>
      <div
        v-else-if="!checkoutSettings.formas_pagamento.length"
        class="catalog-configs-empty"
      >
        Nenhuma forma de pagamento cadastrada para esta empresa.
      </div>
      <div v-else class="catalog-payment-table-wrap">
        <table class="catalog-payment-table">
          <thead>
            <tr>
              <th scope="col">Forma de pagamento</th>
              <th scope="col">Tipo / meio</th>
              <th scope="col">Disponível no catálogo</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="forma in checkoutSettings.formas_pagamento"
              :key="forma.id"
            >
              <td data-label="Forma de pagamento">
                <strong>{{ forma.descricao }}</strong>
              </td>
              <td data-label="Tipo / meio">{{ forma.meio || "—" }}</td>
              <td data-label="Disponível no catálogo">
                <label class="catalog-toggle catalog-toggle--payment">
                  <input
                    type="checkbox"
                    :checked="forma.disponivel_catalogo"
                    :disabled="!!savingPaymentIds[forma.id]"
                    :aria-label="'Disponível no catálogo: ' + forma.descricao"
                    @change="updatePaymentAvailability(forma, $event)"
                  />
                  <span class="catalog-toggle__track">
                    <span class="catalog-toggle__thumb"></span>
                  </span>
                </label>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </article>
  </section>
</template>

<script>
import {
  getCatalogCheckoutSettings,
  getCatalogPaymentMeans,
  createCatalogPaymentMethod,
  saveCatalogCheckoutRule,
  saveCatalogPaymentAvailability,
} from "../services/catalogoDigitalApi";

export default {
  name: "CatalogoDigitalSettingsTab",
  props: {
    active: { type: Boolean, default: false },
  },
  data() {
    return {
      loadingCheckoutSettings: false,
      checkoutSettingsLoaded: false,
      checkoutSettingsError: false,
      savingCheckoutRule: false,
      savingPaymentIds: {},
      creatingPayment: false,
      loadingPaymentMeans: false,
      savingNewPayment: false,
      paymentFormError: "",
      paymentMeans: [],
      paymentIntervals: [
        { id: 1, descricao: "Diário" },
        { id: 2, descricao: "Semanal" },
        { id: 3, descricao: "Quinzenal" },
        { id: 4, descricao: "Mensal" },
        { id: 5, descricao: "Bimestral" },
        { id: 6, descricao: "Trimestral" },
        { id: 7, descricao: "Semestral" },
        { id: 8, descricao: "Anual" },
      ],
      newPayment: {
        descricao: "",
        id_meio_pagamento: null,
        vezes: 1,
        tipo_repeticao: 4,
      },
      checkoutSettings: {
        permitir_pedido_sem_documento: false,
        permitir_pedido_sem_email: false,
        som_novos_pedidos: false,
        formas_pagamento: [],
      },
    };
  },
  watch: {
    active: {
      immediate: true,
      handler(active) {
        if (
          active &&
          !this.checkoutSettingsLoaded &&
          !this.loadingCheckoutSettings
        ) {
          this.loadCheckoutSettings();
        }
      },
    },
  },
  methods: {
    async loadCheckoutSettings() {
      this.loadingCheckoutSettings = true;
      this.checkoutSettingsError = false;
      try {
        const data = await getCatalogCheckoutSettings();
        this.checkoutSettings = {
          permitir_pedido_sem_documento: Boolean(
            data.permitir_pedido_sem_documento
          ),
          permitir_pedido_sem_email: Boolean(data.permitir_pedido_sem_email),
          som_novos_pedidos: Boolean(data.som_novos_pedidos),
          formas_pagamento: data.formas_pagamento || [],
        };
        this.checkoutSettingsLoaded = true;
      } catch (error) {
        this.checkoutSettingsError = true;
        this.notifyError(
          error,
          "Não foi possível carregar as configurações do checkout."
        );
      } finally {
        this.loadingCheckoutSettings = false;
      }
    },
    async togglePaymentForm() {
      this.creatingPayment = !this.creatingPayment;
      this.paymentFormError = "";
      if (!this.creatingPayment || this.paymentMeans.length) return;
      this.loadingPaymentMeans = true;
      try {
        this.paymentMeans = await getCatalogPaymentMeans();
      } catch (error) {
        this.paymentFormError =
          "Não foi possível carregar os meios de pagamento.";
        this.notifyError(error, this.paymentFormError);
      } finally {
        this.loadingPaymentMeans = false;
      }
    },
    async createPayment() {
      this.paymentFormError = "";
      if (
        !this.newPayment.descricao ||
        !this.newPayment.id_meio_pagamento ||
        !Number.isInteger(this.newPayment.vezes) ||
        this.newPayment.vezes < 1 ||
        this.newPayment.vezes > 48 ||
        (this.newPayment.vezes > 1 &&
          !this.paymentIntervals.some(
            (intervalo) => intervalo.id === this.newPayment.tipo_repeticao
          ))
      ) {
        this.paymentFormError = "Revise descrição, meio e número de parcelas.";
        return;
      }
      this.savingNewPayment = true;
      try {
        await createCatalogPaymentMethod({
          descricao: this.newPayment.descricao,
          id_meio_pagamento: this.newPayment.id_meio_pagamento,
          vezes: this.newPayment.vezes,
          tipo_repeticao:
            this.newPayment.vezes === 1 ? 0 : this.newPayment.tipo_repeticao,
          visualizar: true,
          visualizar_app: true,
        });
        await this.loadCheckoutSettings();
        if (this.checkoutSettingsError) {
          this.paymentFormError =
            "Forma criada, mas não foi possível atualizar a lista.";
          return;
        }
        this.creatingPayment = false;
        this.newPayment = {
          descricao: "",
          id_meio_pagamento: null,
          vezes: 1,
          tipo_repeticao: 4,
        };
        this.$toast.success("Forma de pagamento criada no ERP.");
      } catch (error) {
        this.paymentFormError =
          error?.response?.data?.msg ||
          error?.response?.data?.message ||
          "Não foi possível criar a forma de pagamento.";
        this.notifyError(error, this.paymentFormError);
      } finally {
        this.savingNewPayment = false;
      }
    },
    async updateCheckoutRule(field, event) {
      const previous = this.checkoutSettings[field];
      const enabled = event.target.checked;
      if (field === "som_novos_pedidos" && enabled) {
        window.dispatchEvent(new Event("catalogo-digital:liberar-som"));
      }
      this.savingCheckoutRule = true;
      try {
        const data = await saveCatalogCheckoutRule({ [field]: enabled });
        this.checkoutSettings[field] = Boolean(data[field]);
        if (field === "som_novos_pedidos") {
          window.dispatchEvent(
            new CustomEvent("catalogo-digital:som-alterado", {
              detail: { som_novos_pedidos: this.checkoutSettings[field] },
            })
          );
        }
      } catch (error) {
        event.target.checked = previous;
        this.notifyError(error, "Não foi possível salvar a configuração.");
      } finally {
        this.savingCheckoutRule = false;
      }
    },
    async updatePaymentAvailability(forma, event) {
      const previous = forma.disponivel_catalogo;
      const next = event.target.checked;
      this.$set(this.savingPaymentIds, forma.id, true);
      try {
        const data = await saveCatalogPaymentAvailability(forma.id, next);
        forma.disponivel_catalogo = Boolean(data.disponivel_catalogo);
      } catch (error) {
        event.target.checked = previous;
        this.notifyError(
          error,
          "Não foi possível salvar a forma de pagamento."
        );
      } finally {
        this.$delete(this.savingPaymentIds, forma.id);
      }
    },
    notifyError(error, fallback) {
      const message =
        error?.response?.data?.message || error?.message || fallback;
      this.$toast.error(message);
    },
  },
};
</script>

<style scoped>
.catalog-configs-workspace {
  --ink: #172033;
  --muted: #6b7280;
  --line: #e7e9f0;
  --brand: var(--primary-color, #ee7361);
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(0, 3fr);
  align-items: start;
  gap: 18px;
  width: 100%;
}

.catalog-panel {
  min-width: 0;
  padding: 25px;
  border: 1px solid var(--line);
  border-radius: 20px;
  background: #fff;
  box-shadow: 0 8px 28px rgba(31, 38, 61, 0.045);
}

.catalog-panel__heading,
.catalog-panel__heading > div {
  display: flex;
  align-items: center;
  gap: 13px;
}

.catalog-panel__heading {
  justify-content: space-between;
  margin-bottom: 18px;
}

.catalog-panel__heading > div {
  min-width: 0;
}

.catalog-checkout-rules {
  display: grid;
}

.catalog-panel__heading h2 {
  margin: 0;
  color: var(--ink);
  font-size: 18px;
  font-weight: 800;
}

.catalog-panel__heading p {
  margin: 4px 0 0;
  color: var(--muted);
  font-size: 12px;
}

.catalog-section-number {
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  border-radius: 10px;
  color: var(--brand);
  background: color-mix(in srgb, var(--brand) 10%, #fff 90%);
  font-size: 11px;
  font-weight: 900;
}

.catalog-config-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-width: 0;
  padding: 8px 0;
}

.catalog-config-row:not(:last-child) {
  border-bottom: 1px solid #eef0f4;
}

.catalog-config-row strong,
.catalog-payment-table strong {
  color: var(--ink);
  font-size: 13px;
  font-weight: 750;
}

.catalog-config-row p {
  margin: 5px 0 0;
  color: var(--muted);
  font-size: 12px;
  line-height: 1.5;
}

.catalog-toggle {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex: 0 0 auto;
  margin: 0;
  cursor: pointer;
}

.catalog-toggle input {
  position: absolute;
  opacity: 0;
}

.catalog-toggle__track {
  position: relative;
  width: 40px;
  height: 22px;
  border-radius: 99px;
  background: #cbd0da;
  transition: 0.2s;
}

.catalog-toggle__thumb {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  transition: 0.2s;
}

.catalog-toggle input:checked + .catalog-toggle__track {
  background: var(--brand);
}

.catalog-toggle input:checked + .catalog-toggle__track .catalog-toggle__thumb {
  transform: translateX(18px);
}

.catalog-toggle input:focus-visible + .catalog-toggle__track {
  outline: 3px solid color-mix(in srgb, var(--brand) 30%, transparent);
  outline-offset: 3px;
}

.catalog-toggle input:disabled + .catalog-toggle__track {
  cursor: wait;
  opacity: 0.55;
}

.catalog-panel__heading .catalog-button {
  flex: 0 0 auto;
  margin-left: auto;
}

.catalog-button--outline {
  border: 1px solid var(--line);
  color: var(--ink);
  background: #fff;
}

.catalog-payment-form {
  display: flex;
  align-items: end;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 18px;
  padding: 18px;
  border-radius: 14px;
  background: #f7f8fb;
}

.catalog-payment-form label {
  display: grid;
  gap: 6px;
  flex: 1 1 170px;
  color: var(--ink);
  font-size: 12px;
  font-weight: 700;
}

.catalog-payment-form input,
.catalog-payment-form select {
  min-height: 38px;
  min-width: 0;
  padding: 8px 10px;
  border: 1px solid var(--line);
  border-radius: 9px;
  background: #fff;
  font: inherit;
}

.catalog-payment-form__error {
  flex-basis: 100%;
  margin: 0;
  color: #b42318;
  font-size: 12px;
}

.catalog-configs-empty {
  padding: 12px 0;
  color: var(--muted);
  font-size: 13px;
}

.catalog-configs-empty p {
  margin: 0 0 12px;
}

.catalog-button {
  min-height: 38px;
  padding: 0 15px;
  border: 0;
  border-radius: 11px;
  color: #fff;
  background: var(--brand);
  font-size: 13px;
  font-weight: 750;
  cursor: pointer;
}

.catalog-payment-table-wrap {
  width: 100%;
  overflow-x: auto;
}

.catalog-payment-table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
}

.catalog-payment-table th,
.catalog-payment-table td {
  padding: 9px 8px;
  text-align: left;
}

.catalog-payment-table th {
  color: var(--muted);
  font-size: 11px;
  font-weight: 750;
}

.catalog-payment-table td {
  border-top: 1px solid #eef0f4;
  color: #4b5262;
  font-size: 12px;
}

.catalog-payment-table th:first-child {
  width: 45%;
}

.catalog-payment-table th:nth-child(2) {
  width: 35%;
}

.catalog-payment-table th:last-child {
  width: 20%;
}

.catalog-payment-table th:last-child,
.catalog-payment-table td:last-child {
  text-align: right;
}

.catalog-payment-table td:first-child,
.catalog-payment-table td:nth-child(2) {
  overflow-wrap: anywhere;
}

@media (max-width: 1100px) {
  .catalog-configs-workspace {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media (max-width: 768px) {
  .catalog-panel__heading {
    align-items: flex-start;
    flex-wrap: wrap;
  }
}

@media (max-width: 640px) {
  .catalog-panel {
    padding: 19px 16px;
    border-radius: 16px;
  }
  .catalog-config-row {
    align-items: flex-start;
  }
  .catalog-payment-table thead {
    display: none;
  }
  .catalog-payment-table,
  .catalog-payment-table tbody,
  .catalog-payment-table tr,
  .catalog-payment-table td {
    display: block;
    width: 100%;
  }
  .catalog-payment-table tr {
    padding: 12px 0;
    border-top: 1px solid #eef0f4;
  }
  .catalog-payment-table td {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 5px 0;
    border: 0;
    text-align: right;
  }
  .catalog-payment-table td::before {
    content: attr(data-label);
    flex: 0 0 auto;
    color: var(--muted);
    font-size: 11px;
    text-align: left;
  }
  .catalog-payment-table td:last-child {
    width: 100%;
  }
}
</style>
